import { useContext } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import dayjs from "dayjs";

import BaseCard from "../../components/ui/BaseCard";
import BaseNotFound from "../../components/ui/BaseNotFound";
import BaseWrapper from "../../components/ui/BaseWrapper";
import WorkForceTable from "../WorkForce/WorkForceTable";
import { getAdminFreelanceCompanyMembers } from "../../services/api/freelanceCompany";
import { LayoutContext } from "../../context/LayoutContext";
import useAuth from "../../hooks/useAuth";
import { UserType } from "../../utils/enum";
import UserProfileLink from "../../components/ui/UserProfileLink";

import { formatPhoneNumber } from "../../utils/helpers";
const relativeTime = require("dayjs/plugin/relativeTime");
dayjs.extend(relativeTime);

const FreelanceCompanyDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { isBannerVisible } = useContext(LayoutContext);
  const { authRole } = useAuth();
  const isCustomer = authRole === UserType.CUSTOMER;

  const {
    data: companyResponse,
    error: companyError,
    isError: isCompanyUnavailable,
    isPending: isCompanyLoading,
  } = useQuery({
    queryKey: ["freelanceCompanyOwner", id],
    queryFn: () => getAdminFreelanceCompanyMembers(id, { page: 1, limit: 1, approvedOnly: true }),
    enabled: !!id,
    staleTime: 1000 * 60 * 5,
    retry: false,
  });

  const companyName = companyResponse?.data?.companyName;
  const ownerRow = companyResponse?.data?.data?.find((row) => row?.isOwner);
  const owner = ownerRow?.user;
  const ownerName = owner ? `${owner.firstName || ""} ${owner.lastName || ""}`.trim() : "";
  const ownerPhone = formatPhoneNumber(owner?.phoneNumber) || "—";

  const wrapperClass = `bg-[#F6F6F6] sm:fixed sticky 2xl:w-[95.2%] xl:w-[93.2%] lg:w-[90.6%] md:w-[87.5%] sm:w-[95%] w-full h-screen transition-all pt-4 ${
    isBannerVisible ? "top-[104px] sm:mt-0 mt-12" : "top-[42px]"
  }`;

  if (isCompanyUnavailable) {
    return (
      <div className={wrapperClass}>
        <BaseNotFound message={companyError?.response?.data?.message} />
      </div>
    );
  }

  return (
    <div className={wrapperClass}>
      <div className="border-b border-zinc-300 bg-white">
        <BaseWrapper>
          <div className="py-[7px] flex justify-between">
            <h3 className="font-light capitalize">
              {companyName || (isCompanyLoading ? "" : "Vendor")}
            </h3>
          </div>
        </BaseWrapper>
      </div>

      <BaseWrapper>
        <BaseCard className="py-4 sm:px-16 px-5 mt-2 rounded-lg" elevation={1}>
          <div className="border-b mb-4 flex justify-between items-center pb-2">
            <h6 className="font-medium">Company Owner</h6>
            {owner && (
              <span
                className="text-primary text-sm cursor-pointer"
                onClick={() => navigate(`/user/details/profile/${owner.id}`)}
              >
                View Profile
              </span>
            )}
          </div>

          {owner ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 my-1">
              <div className="rounded-md py-1.5">
                <p className="flex items-center gap-2 whitespace-nowrap">
                  <span className="font-medium tracking-tight">Legal Name:</span>
                  <UserProfileLink
                    as="span"
                    userId={ownerName ? owner.id : null}
                    className="font-light truncate capitalize"
                  >
                    {ownerName || "—"}
                  </UserProfileLink>
                </p>
              </div>
              {!isCustomer && (
                <div className="rounded-md py-1.5">
                  <p className="flex items-center gap-2 whitespace-nowrap">
                    <span className="font-medium tracking-tight">Email:</span>
                    <span className="font-light truncate">{owner.email || "—"}</span>
                  </p>
                </div>
              )}
              {!isCustomer && (
                <div className="rounded-md py-1.5">
                  <p className="flex items-center gap-2 whitespace-nowrap">
                    <span className="font-medium tracking-tight">Cell Phone:</span>
                    <span className="font-light truncate">{ownerPhone}</span>
                  </p>
                </div>
              )}
              <div className="rounded-md py-1.5">
                <p className="flex items-center gap-2 whitespace-nowrap">
                  <span className="font-medium tracking-tight">Last Active:</span>
                  <span className="font-light capitalize">
                    {owner.lastActive ? ` ${dayjs(owner.lastActive).fromNow()}` : ""}
                  </span>
                </p>
              </div>
            </div>
          ) : (
            <p className="text-sm text-[#595959]">—</p>
          )}
        </BaseCard>
      </BaseWrapper>

      <WorkForceTable freelanceCompanyId={id} />
    </div>
  );
};

export default FreelanceCompanyDetails;
